import React from 'react';
import { ErrorBoundaryFallback } from '@/components/ui/error-boundary-fallback';
import { createErrorTask } from '@/utils/errorTaskCreation';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);

    // Report to Sentry
    import('@sentry/react').then(({ captureException }) => {
      captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    });

    // Create error task automatically
    createErrorTask(error, errorInfo).catch(err => {
      console.error('Failed to create error task:', err);
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Ensure the error fallback doesn't block pointer events
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <div style={{ pointerEvents: 'auto', position: 'relative' }}>
            <FallbackComponent error={this.state.error!} resetError={this.resetError} />
          </div>
        );
      }

      return (
        <div style={{ pointerEvents: 'auto', position: 'relative' }}>
          <ErrorBoundaryFallback error={this.state.error!} resetError={this.resetError} />
        </div>
      );
    }

    return this.props.children;
  }
}